import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { FileUploadInputComponent } from '../file-upload-input/file-upload-input.component';
import { MediaUtils } from '../../../../../../utils/media-utils';
import { ImageThumbnailCache } from '../../../../../../utils/image-thumbnail-cache';
import { CommonModule } from '@angular/common';
import { FileListItemComponent } from '../../../../../../ui/file-list-item/file-list-item.component';

@Component({
  selector: 'app-file-upload-field',
  templateUrl: './file-upload-field.component.html',
  styleUrls: ['./file-upload-field.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, FileUploadInputComponent, FormsModule, FileListItemComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadFieldComponent),
      multi: true
    }
  ]
})
export class FileUploadFieldComponent implements ControlValueAccessor, OnDestroy {
  @Input() public multiple: boolean = false;
  @Input() public inputName: string = 'file';
  @Input() public accept: string[] = ['image/png', 'image/jpeg'];
  @Input() public maxFileSizeInMb: number = 3;
  @Input() public submitAttempted: boolean = false;
  @Input() public interceptPreviewClick: boolean = false;
  @Input() public interceptRemoveClick: boolean = false;
  @Input() public controlId: string | null = null;
  @Input() control: FormControl<File[]> | null = null;
  @Input() public showThumbnail: boolean = false;
  @Input() hideFileList: boolean = false;

  @Output() public previewClicked: EventEmitter<File> = new EventEmitter<File>();
  @Output() public removeClicked: EventEmitter<File> = new EventEmitter<File>();
  @Output() public selectedFilesChanged = new EventEmitter<File[]>();

  @ViewChild('fileInput') fileInput: FileUploadInputComponent | undefined;

  public onChange: (value: File[]) => void = () => {};
  public onTouched: () => void = () => {};

  public selectedFiles: File[] = [];
  public disabled: boolean = false;
  public replaceFileIndex: number = -1;

  private readonly thumbnailCache = new ImageThumbnailCache();

  public onSelectedFilesChange(files: File[]): void {
    if (!files || !files.length) {
      return;
    }

    if (this.replaceFileIndex > -1) {
      this.selectedFiles[this.replaceFileIndex] = files[0];
      this.replaceFileIndex = -1;
    } else if (this.multiple) {
      const existingKeySet = new Set(this.selectedFiles.map(f => `${f.name}-${f.size}-${f.lastModified}`));
      const newUniqueFiles = files.filter(f => !existingKeySet.has(`${f.name}-${f.size}-${f.lastModified}`));
      this.selectedFiles = [...this.selectedFiles, ...newUniqueFiles];
    } else {
      this.selectedFiles = [files[0]];
    }

    this.thumbnailCache.revokeExcept(this.selectedFiles);
    this.onChange([...this.selectedFiles]);
    this.onTouched();

    if (this.fileInput?.input) {
      this.fileInput.input.nativeElement.value = '';
    }

    this.selectedFilesChanged.emit(this.selectedFiles);
  }

  public onChooseAnotherFile(index: number): void {
    this.replaceFileIndex = index;
    this.fileInput?.input?.nativeElement?.click();
  }

  public removeFile(file: File): void {
    if (this.interceptRemoveClick) {
      this.removeClicked.emit(file);
      return;
    }
    this.selectedFiles = this.selectedFiles.filter(f => f !== file);
    this.thumbnailCache.revokeExcept(this.selectedFiles);
    this.onChange([...this.selectedFiles]);
    this.onTouched();
  }

  public getPreviewLabel(index: number): string {
    return this.getFileError(index)?.maxFileSize ? $localize`Choose a different file` : $localize`Preview`;
  }

  public onFilePreviewClicked(file: File, index: number): void {
    if (this.getFileError(index)?.maxFileSize) {
      this.onChooseAnotherFile(index);
    } else {
      this.previewFile(file);
    }
  }

  public previewFile(file: File): void {
    if (this.interceptPreviewClick) {
      this.previewClicked.emit(file);
      return;
    }
    MediaUtils.openFileInNewTab(file);
  }

  public getThumbnailUrl(file: File): string | null {
    return this.showThumbnail ? this.thumbnailCache.getUrl(file) : null;
  }

  public ngOnDestroy(): void {
    this.thumbnailCache.clear();
  }

  public writeValue(value: File[] | null | undefined): void {
    if (value && value.length) {
      this.selectedFiles = value;
    } else {
      this.selectedFiles = [];
      if (this.fileInput?.input) {
        this.fileInput.input.nativeElement.value = '';
      }
    }
    this.thumbnailCache.revokeExcept(this.selectedFiles);
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.fileInput) {
      this.fileInput.setDisabledState(isDisabled);
    }
  }

  public getFileError(index: number): ValidationErrors | null {
    const errors = this.control?.errors;
    if (!errors) return null;

    const maxFileSizeIndexes = errors.maxFileSize as number[] | undefined;
    if (maxFileSizeIndexes?.includes(index)) return { maxFileSize: true };
    if (errors.formattingError) return { formattingError: true };
    return null;
  }
}
